/**
 * Thin client for the applicability-api Worker (apps/api).
 *
 * Endpoints invoked here mirror apps/api/src/index.ts. Schema validation
 * is performed by the API itself; the web layer trusts the JSON shape
 * but does not re-validate (yet — could mount @regpulse/contracts here
 * later for defence-in-depth, see ADR-0002).
 */

import type {
  ComplianceRequirementCard,
  OpportunityBrief,
  PromotedRule,
  RetrievedContext,
  UseCasePublicSignal,
  Wp29Signal,
} from "@regpulse/contracts";

// The latest REAL WP29 weekly feed snapshot — the actual output the deployed
// wp29-feeder produced and archived to R2 (ADR-0012). Bundled so local review can
// replay real data; not mock. See loadLatestWp29Feed below.
import LATEST_WP29_FEED from "../fixtures/wp29-latest-feed.json";

export interface LinkedSignal {
  ruleId: string;
  signalIds: string[];
  cardTitle: string;
}

export interface SignalBundleMeta {
  bundleId: string;
  fetchedAt: string;
  count: number;
}

/** Mirrors the readinessSummary() block on the API side. */
export interface ReadinessSummary {
  disclaimer: string;
  tracks: {
    legal: { kind: "hard_law"; applicable: number; with_evidence: number };
    evidence_standard: { kind: "evidence_standard"; applicable: number };
    market_force: { kind: "market_force"; applicable: number };
    ecosystem: { kind: "ecosystem_signal"; applicable: number };
    continuing_obligation: { with_obligation: number; total_applicable: number };
  };
  counters: {
    total: number;
    applicable: number;
    hidden_by_config: number;
    unknown_needs_config: number;
  };
}

export interface EvaluateResponse {
  contractVersion: string;
  cards: ComplianceRequirementCard[];
  readiness: ReadinessSummary;
  outcomes: Array<{ ruleId: string; outcome: { category: string; reason?: string } }>;
  linkedSignals: LinkedSignal[];
  signalBundle: SignalBundleMeta | null;
  disclaimer: string;
}

export interface Wp29SignalsResponse {
  bundle: { bundleId: string; fetchedAt: string; sourceLabel: string } | null;
  signals: Wp29Signal[];
  count: number;
  disclaimer: string;
}

export interface Wp29IngestResponse {
  bundleId: string;
  fetchedAt: string;
  signalsLoaded: number;
  affectedRuleIdsUnion: string[];
  disclaimer: string;
}

export interface VocIngestResponse {
  ingested: number;
  candidates: Wp29Signal[];
  channels: Record<"openai" | "exa" | "mcp", string>;
  /** Present for a config×topic sweep (RFC-0008 §2.7): the scan width.
   *  `truncated` = the hard per-request cap clipped the sweep (disclosed, not
   *  hidden); `channelErrors` = per-channel upstream_error counts across the
   *  sweep's searches. */
  scan?: {
    topics: number;
    brands: number;
    searches: number;
    truncated?: boolean;
    channelErrors?: Record<"openai" | "exa" | "mcp", number>;
  };
  disclaimer: string;
}

/** Response from the deployed wp29-feeder worker's manual-trigger endpoint
 *  (audit G5). `ok:false` / non-2xx ⇒ fail-closed (thrown). */
export interface Wp29FeederTriggerResponse {
  ok: boolean;
  weekId?: string;
  count?: number;
  error?: string;
}

const VOC_INGEST_TIMEOUT_MS = 180_000;

export interface RetrievalSearchResponse {
  query: string;
  results: RetrievedContext[];
  count: number;
  disclaimer: string;
}

export interface SeedConfigResponse {
  samples: Record<string, unknown>;
  labels?: Record<string, string>;
  disclaimer: string;
}

export interface CoverageClaimCoreRule {
  ruleId: string;
  jurisdictionCode: string;
  instrument: string;
  officialUrl: string;
  rawSha256: string;
  chunkId: string;
  reviewer: string;
  // The drift-gated generator refuses to emit a core rule that is not
  // proof_complete, so this is always exactly "proof_complete".
  decision: "proof_complete";
  evidencePackSha256: string;
}

export interface CoverageClaimBoundaryRule {
  ruleId: string;
  instrument: string;
  reason: string;
}

export interface CoverageClaimResponse {
  claim: {
    declaredScope: {
      completeJurisdictions: string[];
      euUneceCore: boolean;
      coreRules: CoverageClaimCoreRule[];
    };
    scopeBoundary: CoverageClaimBoundaryRule[];
    advisoryNote: string;
  };
  disclaimer: string;
}

const DEFAULT_BASE = "http://127.0.0.1:8787";
const DEFAULT_TIMEOUT_MS = 15_000;

function baseUrl(): string {
  const fromEnv = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL;
  return fromEnv ?? DEFAULT_BASE;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown) {
    super(`API ${status}`);
    this.name = "ApiError";
  }
}

export class ApiTimeoutError extends Error {
  constructor(public timeoutMs: number, public endpoint: string) {
    super(`API ${endpoint} did not respond within ${timeoutMs}ms`);
    this.name = "ApiTimeoutError";
  }
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new ApiTimeoutError(timeoutMs, url);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchSeedConfigs(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<SeedConfigResponse> {
  const url = `${baseUrl()}/v1/seed-config`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<SeedConfigResponse>(res);
}

export async function fetchCoverageClaim(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<CoverageClaimResponse> {
  const url = `${baseUrl()}/v1/coverage-claim`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<CoverageClaimResponse>(res);
}

export async function evaluate(
  config: unknown,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<EvaluateResponse> {
  const url = `${baseUrl()}/v1/applicability/evaluate`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ config }),
    },
    timeoutMs,
  );
  return jsonOrThrow<EvaluateResponse>(res);
}

export async function triggerVocIngest(
  body: {
    brand: string;
    keyword?: string;
    topicSweep?: boolean;
    competitors?: string[];
    topicIds?: string[];
  },
  timeoutMs = VOC_INGEST_TIMEOUT_MS,
): Promise<VocIngestResponse> {
  const url = `${baseUrl()}/v1/voc/ingest`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
    timeoutMs,
  );
  return jsonOrThrow<VocIngestResponse>(res);
}

/**
 * RFC-0008 §2.7 — sweep VOC across the current vehicle config's APPLICABLE topics
 * (each topic's vocKeywords + brand), auto-attributing results to the topic. No
 * free-text keyword or manual topic pick. Advisory; into the same review queue.
 */
export async function triggerVocConfigSweep(
  brand: string,
  competitors?: string[],
  timeoutMs = VOC_INGEST_TIMEOUT_MS,
): Promise<VocIngestResponse> {
  return triggerVocIngest({ brand, topicSweep: true, competitors }, timeoutMs);
}

/** Response from the TRIS national-draft intake (`POST /v1/tris/ingest`). */
export interface TrisIngestResponse {
  ingested: number;
  candidates: Wp29Signal[];
  disclaimer: string;
}

/**
 * RFC-0007 — paste a TRIS (Directive 2015/1535) notification email; the API
 * parses it into a country-tagged ADVISORY national-draft candidate (same
 * reversible review queue; never auto-promoted). The founder's TRIS mailing-list
 * subscription delivers these emails; forwarding/pasting is the v1 transport.
 */
export async function ingestTrisNotification(
  emailText: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<TrisIngestResponse> {
  const url = `${baseUrl()}/v1/tris/ingest`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ emailText }),
    },
    timeoutMs,
  );
  return jsonOrThrow<TrisIngestResponse>(res);
}

export async function triggerWp29MockIngest(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Wp29IngestResponse> {
  const url = `${baseUrl()}/v1/wp29/mock-ingest`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    },
    timeoutMs,
  );
  return jsonOrThrow<Wp29IngestResponse>(res);
}

/** Response from the WP29 native-feed intake (`POST /v1/wp29/ingest`). The route
 *  also returns a `candidates` array the feed loader does not use. */
export interface Wp29DraftIngestResponse {
  ingested: number;
  candidates?: Wp29Signal[];
  disclaimer: string;
}

/** weekId of the bundled real WP29 feed snapshot (for honest UI labelling). */
export const LATEST_WP29_FEED_WEEK = (LATEST_WP29_FEED as { weekId: string }).weekId;

/**
 * Load the latest REAL WP29 weekly feed snapshot into core. This is the actual
 * output the deployed wp29-feeder produced and archived to R2 as
 * wp29/<week>.json (ADR-0012) — NOT mock data. Posts through the SAME
 * `/v1/wp29/ingest` path the live feeder uses, so every row becomes an advisory
 * `draft`/`proposed` candidate linked to its topic via the spine, and MERGES
 * into the review bundle (it does not wipe collected VOC).
 *
 * Why a bundled snapshot for local review: the deployed feeder runs on Cloudflare
 * and pushes to the DEPLOYED core via an internal service binding — it cannot
 * reach a local dev core at localhost. So for local review we replay the real
 * archived feed. The true live weekly pull runs in production (feeder cron →
 * deployed core), or via the live-feeder button when VITE_WP29_FEEDER_URL and a
 * reachable core are configured. Advisory only; never canonical (ADR-0002).
 */
export async function loadLatestWp29Feed(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Wp29DraftIngestResponse> {
  const url = `${baseUrl()}/v1/wp29/ingest`;
  const feed = LATEST_WP29_FEED as { weekId: string; signals: unknown[] };
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ weekId: feed.weekId, signals: feed.signals }),
    },
    timeoutMs,
  );
  return jsonOrThrow<Wp29DraftIngestResponse>(res);
}

/**
 * Trigger the DEPLOYED wp29-feeder worker (audit G5) to pull the live weekly
 * WP29 feed and push signed advisory candidates into core. The feeder is a
 * separate worker addressed by `VITE_WP29_FEEDER_URL` (NOT the core API base).
 *
 * Fail-closed: throws an `ApiError` when the feeder URL is unconfigured, when
 * the feeder is unreachable, or when it returns a non-2xx / `ok:false` body —
 * the caller surfaces the failure rather than pretending the pull succeeded.
 * The mock path (`triggerWp29MockIngest`) stays for local dev / demos.
 */
export async function triggerWp29LiveFeeder(
  feederUrl: string | undefined = (
    import.meta as { env?: { VITE_WP29_FEEDER_URL?: string } }
  ).env?.VITE_WP29_FEEDER_URL,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Wp29FeederTriggerResponse> {
  if (!feederUrl) {
    // Config error, not an API error — throw a plain Error so the descriptive
    // message reaches the UI (callers read `.message`).
    throw new Error(
      "WP29 live feeder is not configured (VITE_WP29_FEEDER_URL unset); use mock ingest in dev.",
    );
  }
  const res = await fetchWithTimeout(
    feederUrl,
    { method: "POST", headers: { "content-type": "application/json" }, body: "{}" },
    timeoutMs,
  );
  const data = await jsonOrThrow<Wp29FeederTriggerResponse>(res);
  if (!data.ok) throw new ApiError(res.status, data);
  return data;
}

/** Reassign a VOC signal's topic attribution (replaces its market_voice spine
 *  edges) — human curation for vocabulary-gap / sticky-selection misattribution. */
export async function reassignSignalTopics(
  signalId: string,
  topicIds: string[],
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<{ id: string; topicIds: string[] }> {
  const url = `${baseUrl()}/v1/signals/${encodeURIComponent(signalId)}/reassign-topics`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ topicIds }),
    },
    timeoutMs,
  );
  return jsonOrThrow<{ id: string; topicIds: string[] }>(res);
}

export async function fetchWp29Signals(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Wp29SignalsResponse> {
  const url = `${baseUrl()}/v1/wp29/signals`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<Wp29SignalsResponse>(res);
}

export async function searchRetrievedContext(
  query: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<RetrievalSearchResponse> {
  const url = `${baseUrl()}/v1/retrieval/search?q=${encodeURIComponent(query)}`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<RetrievalSearchResponse>(res);
}

// ─── Opportunity lens (RFC-0005 Phase C / audit G7) — advisory, never a verdict ──

export interface OpportunityRadarEntry {
  topicId: string;
  domainId: string;
  opportunityType: "opportunity" | "risk" | "watch" | "none";
  /** Three-axis summary surfaced by the radar endpoint (audit G8 cross-analysis
   *  matrix). Optional so existing consumers/mocks stay valid. */
  strictness?: "strict" | "moderate" | "light" | "none";
  direction?: "tightening" | "loosening" | "unclear" | "new" | "none";
  intensity?: "high" | "med" | "low" | "none";
  /** Signal counts on this topic (audit G8 bubble chart): law rules, draft/
   *  proposed signals, market-voice signals. Optional so older mocks stay valid. */
  ruleCount?: number;
  draftCount?: number;
  intelCount?: number;
  /** Content-first redesign: the most-recent draft/proposed signal on this
   *  topic — the headline the surfaces lead with. OMITTED when none exists. */
  latestDraft?: {
    title: string;
    instrument: string;
    changeKind: "draft" | "proposed";
    effectiveDate: string | null;
    fetchedAt: string;
    sourceUrl: string;
  };
  /** One verbatim market-voice quote (most recent), when any pain exists. */
  painSample?: { quote: string; fetchedAt: string };
}

export interface OpportunityRadarResponse {
  radar: OpportunityRadarEntry[];
  disclaimer: string;
}

export interface OpportunityBriefResponse {
  brief: OpportunityBrief;
  sources: {
    cards: number;
    draftSignals: number;
    intelSignals: number;
    raglight: number;
  };
  disclaimer: string;
}

/** Cross-topic opportunity radar (advisory lens, never a verdict — ADR-0002). */
export async function fetchOpportunityRadar(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<OpportunityRadarResponse> {
  const url = `${baseUrl()}/v1/analysis/opportunity?radar=true`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<OpportunityRadarResponse>(res);
}

/** Single-topic three-axis (current law × draft trajectory × intel) brief.
 *  Runs LIVE content reasoning (LLM, 18–45s server-side) — the default 15s
 *  client timeout aborted real analyses mid-flight (founder hit this). */
const BRIEF_TIMEOUT_MS = 60_000;

export async function fetchOpportunityBrief(
  topicId: string,
  timeoutMs = BRIEF_TIMEOUT_MS,
): Promise<OpportunityBriefResponse> {
  const url = `${baseUrl()}/v1/analysis/opportunity?topic=${encodeURIComponent(topicId)}`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<OpportunityBriefResponse>(res);
}

/** Persist tier chosen on accept (B3). canonical = the promote record; raglight
 *  = also index the source as advisory context (never canonical). */
export type PersistTier = "canonical" | "raglight";

export interface ReviewDecisionResponse {
  signalId: string;
  decision: "promoted" | "dismissed" | "reverted";
  newLifecycleState: "promoted" | "dismissed" | "candidate";
  auditEntry: {
    signalId: string;
    decision: "promoted" | "dismissed" | "reverted";
    reviewer: string;
    decidedAt: string;
    notes?: string;
    persistTier?: PersistTier;
    persistMode?: string;
  };
  // promote (raglight tier) → advisory-index outcome; revert (raglight) → rollback.
  persist?: { tier: PersistTier; mode: string; trackId?: string };
  persistRollback?: { tier: "raglight"; mode: string };
  // D3: promote(canonical) with an authored rule materialises it; revert removes it.
  promotedRuleId?: string;
  promotedRulesRemoved?: string[];
  disclaimer: string;
}

async function postReviewDecision(
  signalId: string,
  kind: "promote" | "dismiss" | "revert",
  reviewer: string,
  notes: string | undefined,
  timeoutMs: number,
  persistTier?: PersistTier,
  promotedRule?: PromotedRule,
): Promise<ReviewDecisionResponse> {
  const url = `${baseUrl()}/v1/signals/${encodeURIComponent(signalId)}/${kind}`;
  const body: {
    reviewer: string;
    notes?: string;
    persistTier?: PersistTier;
    promotedRule?: PromotedRule;
  } = { reviewer, notes };
  if (persistTier !== undefined) body.persistTier = persistTier;
  if (promotedRule !== undefined) body.promotedRule = promotedRule;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
    timeoutMs,
  );
  return jsonOrThrow<ReviewDecisionResponse>(res);
}

/** Accept a candidate. Optional `persistTier` (B3): "canonical" (default) is the
 *  promote record; "raglight" also indexes the source as advisory context.
 *  Trailing optionals are an options object (not positional) so this never
 *  collides with dismissSignal's signature through a shared union call. */
export function promoteSignal(
  signalId: string,
  reviewer: string,
  notes?: string,
  opts: { persistTier?: PersistTier; timeoutMs?: number; promotedRule?: PromotedRule } = {},
): Promise<ReviewDecisionResponse> {
  return postReviewDecision(
    signalId,
    "promote",
    reviewer,
    notes,
    opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    opts.persistTier,
    opts.promotedRule,
  );
}

export function dismissSignal(
  signalId: string,
  reviewer: string,
  notes?: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<ReviewDecisionResponse> {
  return postReviewDecision(signalId, "dismiss", reviewer, notes, timeoutMs);
}

/** Reverse a finalised decision back to candidate (B1, RFC-0002). */
export function revertSignal(
  signalId: string,
  reviewer: string,
  notes?: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<ReviewDecisionResponse> {
  return postReviewDecision(signalId, "revert", reviewer, notes, timeoutMs);
}

export interface AcceptUseCaseResponse {
  accepted: boolean;
  useCaseSignal: UseCasePublicSignal;
  disclaimer: string;
}

/** D4: accept a discovered candidate into U "Case intelligence" — a THIRD advisory destination
 *  (never canonical, never the board). The signal lands `reviewedBy: null` (unverified). */
export async function acceptSignalAsUseCase(
  signalId: string,
  reviewer: string,
  useCaseId?: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<AcceptUseCaseResponse> {
  const url = `${baseUrl()}/v1/signals/${encodeURIComponent(signalId)}/accept-use-case`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reviewer, ...(useCaseId ? { useCaseId } : {}) }),
    },
    timeoutMs,
  );
  return jsonOrThrow<AcceptUseCaseResponse>(res);
}

export interface UseCaseSignalsResponse {
  signals: UseCasePublicSignal[];
  count: number;
  disclaimer: string;
}

/** D4: list the advisory U "Case intelligence" signals accepted from discovery. */
export async function fetchUseCaseSignals(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<UseCaseSignalsResponse> {
  const url = `${baseUrl()}/v1/use-case-signals`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<UseCaseSignalsResponse>(res);
}

export interface DiscoveryRunResponse {
  query: string;
  candidates: Wp29Signal[];
  advisorySummary: { summary: string; isVerdict: boolean; model: string };
  sources: { exa: number; raglight: number; llm: string };
  mode: { exa: string; raglight: string; llm: string };
  disclaimer: string;
}

/** Discovery chains exa + RAGLight + an LLM call server-side (each up to ~10-15s),
 *  so its client timeout is longer than the default 15s to avoid spurious aborts
 *  under live keys (RFC-0003 D0). */
const DISCOVERY_TIMEOUT_MS = 45_000;

/** Run the discovery pipeline (exa + RAGLight + LLM-advisory → candidate signals
 *  enqueued for review). Advisory only; human review is the only promotion path. */
export async function runDiscovery(
  query: string,
  timeoutMs = DISCOVERY_TIMEOUT_MS,
): Promise<DiscoveryRunResponse> {
  const url = `${baseUrl()}/v1/discovery/run`;
  const res = await fetchWithTimeout(
    url,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ query }),
    },
    timeoutMs,
  );
  return jsonOrThrow<DiscoveryRunResponse>(res);
}

export interface EvidencePackBuildResponse {
  pack: Record<string, unknown> & { sha256: string; disclaimer: string };
  sha256: string;
  r2Key: string | null;
  disclaimer: string;
}

/**
 * Server-side evidence-pack build (W7-slice-b2). Returns `null` on any
 * failure (network, 4xx, 5xx) so the caller can fall back to the
 * client-side `downloadJsonPack` path with `sha256: null`. The chip
 * the UI renders ("sha256 ✓ verified" vs "advisory hash") reflects
 * which path actually fired.
 */
export async function buildEvidencePack(
  input: unknown,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<EvidencePackBuildResponse | null> {
  const url = `${baseUrl()}/v1/evidence-pack/build`;
  try {
    const res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
      timeoutMs,
    );
    if (!res.ok) return null;
    return (await res.json()) as EvidencePackBuildResponse;
  } catch {
    return null;
  }
}

/**
 * Minimal projection of a registry rule, used by the Compliance Horizon to
 * read real `temporal` dates + a source for citation. The web layer trusts
 * the JSON shape (the API validates); fields are optional/nullable because
 * the registry populates them sparsely and the horizon must render ONLY the
 * dates that genuinely exist (zero inferred dates — ADR-0007, Phase 2).
 */
export interface SeedRuleTemporal {
  entry_into_force?: string | null;
  applies_to_new_types_from?: string | null;
  applies_to_all_new_vehicles_from?: string | null;
  applies_to_first_registration_from?: string | null;
  applies_from_generic?: string | null;
  effective_to?: string | null;
  small_volume_derogation_until?: string | null;
  notes?: string | null;
}

export interface SeedRuleSource {
  label?: string;
  reference?: string;
  official_url?: string | null;
}

export interface SeedRule {
  stable_id: string;
  title: string;
  lifecycle_state?: string;
  temporal?: SeedRuleTemporal | null;
  sources?: SeedRuleSource[];
}

export interface SeedRulesResponse {
  rules: SeedRule[];
  count: number;
  disclaimer: string;
}

export async function fetchSeedRules(
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<SeedRulesResponse> {
  const url = `${baseUrl()}/v1/seed-rules`;
  const res = await fetchWithTimeout(url, {}, timeoutMs);
  return jsonOrThrow<SeedRulesResponse>(res);
}
